/**
 * src/pages/Ejecuciones.jsx
 * Fase 3: Actividad y Programación, fundidas en una línea de tiempo.
 *
 * La idea que valida el diseño: una sola lista con el AHORA como pliegue.
 * Arriba lo que viene, abajo lo que pasó. No son dos pantallas puestas una
 * junto a otra — es el mismo material leído en dos direcciones.
 *
 * Tres pestañas (Manuel, 28-08): la línea de tiempo abre; el registro del
 * correo semanal y la sonda de tiendas dejan de alargar el scroll y viven en
 * las suyas. Los destinos aterrizan con ?pestana= puesto.
 */
import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { Header } from '../components/layout/Header'
import { Cifra } from '../components/ui/Cifra'
import { Pildora } from '../components/ui/Pildora'
import { BarraPestanas } from '../components/ui/Pestanas'
import { NotaAccion } from '../components/ui/NotaAccion'
import { BloqueJSON } from '../components/ui/BloqueJSON'
import { SelectorTenant } from '../components/ejecuciones/SelectorTenant'
import { ResumenSemanal } from '../components/ejecuciones/ResumenSemanal'
import { EstadoPlantillas } from '../components/ejecuciones/EstadoPlantillas'
import { RevisionCompetidores } from '../components/ejecuciones/RevisionCompetidores'
import { useEjecuciones } from '../hooks/useAdminPanel'
import { getSondaTiendas, getSondaFicha, getFotoFija, getFotoFijaVersionadas,
         getColaConvenios } from '../api/scraping'
import { getLicitaciones, adoptarLicitacion,
         getConveniosConLicitacion } from '../api/licitaciones'
import { diaISOChile, horaCL } from '../lib/horaChile'
import { clp } from '../lib/cifras'

const COLOR_ESTADO = {
  ok: 'var(--color-ok)',
  error: 'var(--color-error)',
  running: 'var(--color-running)',
}

const TIPOS = [
  { clave: null, etiqueta: 'todo' },
  { clave: 'precios', etiqueta: 'precios' },
  { clave: 'ocs', etiqueta: 'órdenes de compra' },
]

const PESTANAS = [
  { clave: 'linea', label: 'Línea de tiempo' },
  { clave: 'correo', label: 'Correo semanal' },
  { clave: 'sonda', label: 'Sonda de tiendas' },
  { clave: 'plantillas', label: 'Plantillas' },
  { clave: 'competidores', label: 'Competidores' },
]

// Ventanas elegibles del pasado. El backend acepta 1-90; estas tres bastan
// para «qué pasó esta semana / quincena / mes» sin un campo libre.
const VENTANAS = [7, 14, 30]

// La fecha de cada día viene del backend YA como día chileno (29-08); el
// «hoy/ayer» se compara contra el día chileno, no el del navegador.
function _rotuloDia(iso) {
  const [y, m, dd] = iso.split('-').map(Number)
  const d = new Date(y, (m || 1) - 1, dd || 1)
  if (Number.isNaN(d.getTime())) return iso
  const largo = d.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' })
  if (iso === diaISOChile()) return `Hoy · ${largo}`
  if (iso === diaISOChile(-1)) return `Ayer · ${largo}`
  return largo.charAt(0).toUpperCase() + largo.slice(1)
}

/** Hoy y ayer llegan abiertos; el resto de la ventana, plegado a una línea. */
function _esReciente(iso) {
  return iso === diaISOChile() || iso === diaISOChile(-1)
}

// La Pildora vive ahora en components/ui/Pildora.jsx: la tabla de churn ganó
// filtros con el mismo patrón, y dos usos reales son un componente.

/** Una ejecución que aún no ha ocurrido: círculo hueco. */
function Proxima({ dato }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '78px 12px 1fr auto',
      gap: 'var(--space-4)', alignItems: 'baseline', padding: 'var(--space-3) 0',
    }}>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
        color: 'var(--color-text-2)', fontVariantNumeric: 'tabular-nums',
      }}>
        {horaCL(dato.cuando) || '—'}
      </span>
      <span style={{
        width: '7px', height: '7px', borderRadius: 'var(--radius-pill)',
        border: '1.5px solid var(--color-border-2)', display: 'block',
        marginTop: '5px', flexShrink: 0,
      }} />
      <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-text)' }}>
        {dato.tipo}{' '}
        <span style={{ color: 'var(--color-text-3)' }}>·</span>{' '}
        <span style={{ color: 'var(--color-text-2)' }}>{dato.tenant}</span>
      </span>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
        color: 'var(--color-text-3)', textAlign: 'right', whiteSpace: 'nowrap',
      }}>
        {[dato.falta, dato.frecuencia].filter(Boolean).join(' · ') || 'sin hora'}
      </span>
    </div>
  )
}

/** Una ejecución que ya ocurrió: círculo relleno del color de su estado. */
function Pasada({ dato, onIr }) {
  const color = COLOR_ESTADO[dato.estado] || COLOR_ESTADO.ok
  const cola = { ok: '', error: ' — falló', running: ' — corriendo' }[dato.estado] || ''
  return (
    <div
      onClick={() => dato.cliente_id && onIr?.(dato.cliente_id)}
      style={{
        display: 'grid', gridTemplateColumns: '78px 12px 1fr auto',
        gap: 'var(--space-4)', alignItems: 'baseline',
        padding: 'var(--space-3) 0', borderTop: '1px solid var(--color-surface-2)',
        cursor: dato.cliente_id ? 'pointer' : 'default',
        transition: 'opacity var(--t-base) var(--ease)',
      }}
      onMouseEnter={e => { if (dato.cliente_id) e.currentTarget.style.opacity = '0.7' }}
      onMouseLeave={e => { e.currentTarget.style.opacity = '1' }}
    >
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
        color: 'var(--color-text-3)', fontVariantNumeric: 'tabular-nums',
      }}>
        {dato.hora}
      </span>
      <span style={{
        width: '7px', height: '7px', borderRadius: 'var(--radius-pill)',
        background: color, display: 'block', marginTop: '5px', flexShrink: 0,
      }} />
      <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-text)' }}>
        {dato.tipo}{' '}
        <span style={{ color: 'var(--color-text-3)' }}>·</span>{' '}
        <span style={{ color: 'var(--color-text-2)' }}>{dato.tenant}</span>
        <span style={{ color }}>{cola}</span>
        {dato.nota && (
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
            color: dato.estado === 'error' ? 'var(--color-text-3)' : 'var(--color-error)',
            marginLeft: 'var(--space-2)',
          }}>
            {dato.nota}
          </span>
        )}
      </span>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
        color: 'var(--color-text-3)', textAlign: 'right', whiteSpace: 'nowrap',
      }}>
        {dato.duracion || '—'}
      </span>
    </div>
  )
}

// ── Sonda de tiendas de Convenio Marco ───────────────────────────────────────
// Qué convenios puede cubrir el MOTOR, que es distinto de en cuáles factura un
// tenant (eso lo dice el bloque `convenios` del diagnóstico de cada uno).
// El survey que teníamos es de 2026-06-29 y sus datos crudos NO se commitearon,
// así que la única forma de saber qué sigue vigente es volver a medir.
function SondaTiendas() {
  const [d, setD] = useState(null)
  const [cargando, setCargando] = useState(false)

  async function medir() {
    setCargando(true); setD(null)
    try { setD(await getSondaTiendas()) }
    catch (e) { setD({ error: e.response?.data?.detail || 'No se pudo sondear' }) }
    finally { setCargando(false) }
  }

  const tiendas = Array.isArray(d?.tiendas) ? d.tiendas : []

  return (
    <div>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
        letterSpacing: '0.13em', textTransform: 'uppercase',
        color: 'var(--color-text-3)', paddingBottom: 'var(--space-2)',
      }}>
        Tiendas de Convenio Marco
      </div>
      {/* Dos columnas (Manuel, 31-08, opción C): los datos a la izquierda,
          la explicación y la lectura en la columna estrecha de la derecha. */}
      <div className="cm-rejilla-nota">
        <div style={{ minWidth: 0 }}>
          <button
            onClick={medir}
            disabled={cargando}
            style={{
              padding: '7px 16px', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-base)',
              border: '1px solid var(--color-border)', background: 'transparent',
              color: 'var(--color-text-2)', cursor: cargando ? 'default' : 'pointer',
              fontFamily: 'var(--font-ui)',
            }}
          >
            {cargando ? 'Midiendo el portal… (puede tardar un par de minutos)' : 'Revalidar el survey'}
          </button>

          {d?.error && (
            <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)',
                          color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>✗ {d.error}</div>
          )}

          {tiendas.length > 0 && (
            <div style={{ marginTop: 'var(--space-3)', overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 'var(--text-sm)', width: '100%' }}>
                <thead>
                  <tr style={{ color: 'var(--color-text-3)', textAlign: 'left' }}>
                    {['Tienda', 'Veredicto', 'Vendedores', 'region_ids', '¿Encajan?'].map(h => (
                      <th key={h} style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)',
                                           fontSize: 'var(--text-xs)', fontWeight: 500,
                                           borderBottom: '1px solid var(--color-border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tiendas.map(t => (
                    <tr key={t.tienda} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>{t.tienda}</td>
                      <td style={{ padding: '6px 10px' }}>
                        {t.alcanzable ? (t.veredicto || '—') : 'no se pudo mirar'}
                      </td>
                      <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>{t.n_sellers ?? '—'}</td>
                      <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>{t.n_region_ids ?? '—'}</td>
                      <td style={{ padding: '6px 10px' }}>
                        {t.region_ids_encajan === true ? 'sí'
                          : t.region_ids_encajan === false ? 'no' : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <aside className="cm-nota-lateral" style={{ minWidth: 0 }}>
          <NotaAccion
            que="Recorre las 12 tiendas de Convenio Marco que conocemos y comprueba cuáles sigue pudiendo leer nuestro motor de precios. El survey escrito es de junio y sus datos crudos no se guardaron, así que la única forma de saber qué sigue vigente es volver a medir."
            coste="un par de minutos — unas 80 peticiones al portal. No cambia nada: solo lee."
            mirar="«Veredicto» decide si una tienda es portable. `no se pudo mirar` NO significa incompatible: significa que la tienda no respondió o cambió de slug, y hay que repetir."
          />
          {tiendas.length > 0 && d.lectura && (
            <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)',
                          borderTop: '1px solid var(--color-border)',
                          fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
                          lineHeight: 1.5 }}>
              {d.lectura}
            </div>
          )}
        </aside>
      </div>

      <ColaConvenios />
      <MapaLicitaciones />
      <SondaFicha />
      <FotoFija />
    </div>
  )
}


// ⚠️ Un importe de tienda puede venir casi entero de tenants dados de baja
// —`aseo3` son $4.005 M de los que $3.938 M son de uno que se dio de baja—, y sin
// decirlo la cifra se lee como facturación viva. La columna «Tenants» ya marca
// «(de baja)», pero eso no reparte el dinero, que es lo que se compara.
// Si el convenio sigue vivo para ese tenant: la mitad de decidir si su dinero se
// puede recuperar. ⚠️ Sin fecha NO es «sin órdenes recientes»: las filas que la
// sonda guardó antes del 11-09 no la traen, y eso se dice como lo que es (§3).
function textoUltimaOC(c) {
  if (!c.ultima_oc) return 'última OC sin medir — volver a sondear'
  const [y, m, d] = String(c.ultima_oc).split('-')
  return `última OC ${d}-${m}-${y}`
}

function Reparto({ g }) {
  if (g.monto_activos == null || g.monto_activos === g.monto) return null
  return (
    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
                  fontFamily: 'var(--font-ui)', marginTop: 'var(--space-05)' }}>
      {g.monto_activos === 0
        ? 'todo de tenants dados de baja'
        : `de tenants activos: $${g.monto_activos.toLocaleString('es-CL')}`}
    </div>
  )
}


// ── Dinero atascado por tienda, sumando TODOS los tenants ────────────────────
// La decisión que depende de esto no es de un tenant: es qué tienda del portal
// portamos después, y esa se toma con la cartera entera. Una tienda con $6 M en
// un tenant no justifica el trabajo; la misma en cinco tenants sumando $200 M,
// sí — y mirando tenant a tenant esa diferencia no se ve.
//
// ⚠️ El aviso va ARRIBA de las cifras a propósito. Esta vista tiene tres formas
// de valer poco: que falten tenants por sondear, que los sondeados sean
// anteriores a que guardáramos la cola, o que de verdad no haya nada atascado.
// Las tres pintan un total pequeño, y un total pequeño no se lee como «faltan
// datos» sino como «no vale la pena portar esa tienda». Poner el aviso debajo
// sería dejar que la cifra decida antes de que se lea la advertencia (§3).
function ColaConvenios() {
  const [d, setD] = useState(null)
  const [cargando, setCargando] = useState(false)

  const sumar = useCallback(async () => {
    setCargando(true)
    try { setD(await getColaConvenios()) }
    catch (e) { setD({ error: e.response?.data?.detail || 'No se pudo sumar la cola' }) }
    finally { setCargando(false) }
  }, [])

  // Se carga sola: no mide nada, solo lee lo ya guardado. Pedir un clic para
  // algo gratis es hacer trabajar al que mira sin cobrar nada a cambio.
  useEffect(() => { sumar() }, [sumar])

  const tiendas = Array.isArray(d?.tiendas) ? d.tiendas : []
  const sinTienda = Array.isArray(d?.sin_tienda) ? d.sin_tienda : []
  const encontradas = Array.isArray(d?.encontradas) ? d.encontradas : []
  // ⚠️ Sin `|| {}`. Rellenar la respuesta con un objeto vacío convierte «no hay
  // respuesta» en «respuesta con todo a cero», y aquí un cero se lee como «no
  // hay dinero atascado en ninguna tienda» — la conclusión más cara posible de
  // esta pantalla. Que falte, y que se note que falta.
  const tot = d?.totales
  const av = d?.avisos
  const faltan = [...(av?.tenants_sin_sondear || []), ...(av?.tenants_con_sonda_sin_cola || [])]
  // Los dados de baja entran (sus órdenes son evidencia de mercado) pero se
  // marcan: mezclarlos con facturación viva sería otra cifra que engaña.
  const bajas = new Set(av?.tenants_inactivos_incluidos || [])
  const nombreTenant = (t) => (bajas.has(t) ? `${t} (de baja)` : t)

  return (
    <div style={{ marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)',
                  borderTop: '1px solid var(--color-border)' }}>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
        letterSpacing: '0.13em', textTransform: 'uppercase',
        color: 'var(--color-text-3)', paddingBottom: 'var(--space-2)',
      }}>
        Dinero atascado por tienda · todos los tenants
      </div>
      <div className="cm-rejilla-nota">
        <div style={{ minWidth: 0 }}>
          <button
            onClick={sumar}
            disabled={cargando}
            style={{
              padding: '7px 16px', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-base)',
              border: '1px solid var(--color-border)', background: 'transparent',
              color: 'var(--color-text-2)', cursor: cargando ? 'default' : 'pointer',
              fontFamily: 'var(--font-ui)',
            }}
          >
            {cargando ? 'Sumando…' : 'Recalcular'}
          </button>

          {d?.error && (
            <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)',
                          color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>✗ {d.error}</div>
          )}

          {tot && faltan.length > 0 && (
            <div style={{
              marginTop: 'var(--space-3)', padding: 'var(--space-2) var(--space-3)',
              borderRadius: 'var(--radius-sm)', background: 'var(--color-warn-soft)',
              border: '1px solid var(--color-warn-line)',
              fontSize: 'var(--text-sm)', lineHeight: 1.55, color: 'var(--color-text-2)',
            }}>
              <strong style={{ color: 'var(--color-warn)' }}>
                Estas cifras suman {tot.n_tenants_sumados} de {d.tenants_totales} tenants.
              </strong>{' '}
              El resto no está contado, así que el total es un <em>mínimo</em>, no una medida.
              {(av?.tenants_sin_sondear || []).length > 0 && (
                <div style={{ marginTop: 'var(--space-05)' }}>
                  Sin sondear: <span style={{ fontFamily: 'var(--font-mono)' }}>
                    {av.tenants_sin_sondear.join(', ')}</span>
                </div>
              )}
              {(av?.tenants_con_sonda_sin_cola || []).length > 0 && (
                <div style={{ marginTop: 'var(--space-05)' }}>
                  Sondeados <em>antes</em> de que la sonda guardara el detalle — hay que
                  volver a sondearlos: <span style={{ fontFamily: 'var(--font-mono)' }}>
                    {av.tenants_con_sonda_sin_cola.join(', ')}</span>
                </div>
              )}
            </div>
          )}

          {/* ⚠️ Que NO salga el aviso amarillo es justo la señal de que las cifras
              sirven — y una señal que consiste en la ausencia de algo no se ve.
              Manuel preguntó dónde estaba el aviso teniéndolo todo completo. */}
          {tot && faltan.length === 0 && (
            <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)',
                          color: 'var(--color-ok)', lineHeight: 1.55 }}>
              ✓ Suma los {d.tenants_totales} tenants: no falta ninguno por sondear.
            </div>
          )}

          {tot && (
            <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)',
                          color: 'var(--color-text-2)', lineHeight: 1.6 }}>
              <div>
                Con tienda identificada:{' '}
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text)' }}>
                  {clp(tot.monto_con_tienda_identificada)}</span>
                {'  ·  '}sin atribuir:{' '}
                <span style={{ fontFamily: 'var(--font-mono)' }}>
                  {clp(tot.monto_sin_tienda)}</span>
              </div>
              {tot.monto_de_tenants_inactivos > 0 && (
                <div style={{ color: 'var(--color-text-3)', fontSize: 'var(--text-xs)' }}>
                  De las DOS cifras de arriba, {clp(tot.monto_de_tenants_inactivos)} son
                  de tenants dados de baja ({[...bajas].join(', ')}). Cuentan para decidir
                  qué tienda portar —sus órdenes ocurrieron— pero no son facturación viva.
                  Las tablas de abajo llevan su propio reparto.
                </div>
              )}
              {tot.monto_pospuesto > 0 && (
                <div style={{ color: 'var(--color-text-3)', fontSize: 'var(--text-xs)' }}>
                  Y {clp(tot.monto_pospuesto)} que ni se llegaron a
                  mirar (quedaron fuera del tope de convenios por sondeo): eso no es cola,
                  es sondeo pendiente.
                </div>
              )}
              {tot.monto_omitido_por_tamano > 0 && (
                <div style={{ color: 'var(--color-text-3)', fontSize: 'var(--text-xs)' }}>
                  Incluye {clp(tot.monto_omitido_por_tamano)} de
                  convenios cuyo detalle no cupo en el resumen (el dinero sí está contado).
                </div>
              )}
              {av?.convenios_ya_mapeados_descartados > 0 && (
                <div style={{ color: 'var(--color-text-3)', fontSize: 'var(--text-xs)' }}>
                  {av.convenios_ya_mapeados_descartados} convenio(s) de la cola ya se
                  aprendieron después del sondeo y se han descartado.
                </div>
              )}
            </div>
          )}

          {tiendas.length > 0 && (
            <div style={{ marginTop: 'var(--space-3)', overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 'var(--text-sm)', width: '100%' }}>
                <thead>
                  <tr style={{ color: 'var(--color-text-3)', textAlign: 'left' }}>
                    {['Tienda', 'Dinero atascado', 'Convenios', 'Tenants'].map(h => (
                      <th key={h} style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)',
                                           fontSize: 'var(--text-xs)', fontWeight: 500,
                                           borderBottom: '1px solid var(--color-border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tiendas.map(g => (
                    <tr key={g.tienda} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>{g.tienda}</td>
                      <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)',
                                   color: 'var(--color-running)' }}>
                        {clp(g.monto)}
                        <Reparto g={g} />
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <details>
                          <summary style={{ cursor: 'pointer' }}>{g.n_convenios}</summary>
                          <div style={{ paddingTop: 'var(--space-1)',
                                        fontSize: 'var(--text-xs)', lineHeight: 1.6 }}>
                            {g.convenios.map((c, i) => (
                              <div key={`${c.tenant}-${c.convenio}-${i}`}>
                                <span style={{ fontFamily: 'var(--font-mono)' }}>{c.convenio}</span>
                                {' · '}{c.titulo || '—'}
                                {' · '}<span style={{ fontFamily: 'var(--font-mono)' }}>
                                  {clp(c.monto)}</span>
                                {' · '}{nombreTenant(c.tenant)}
                                {Object.entries(c.urls || {}).map(([slug, url]) => (
                                  <span key={slug}>
                                    {' · '}
                                    <a href={url} target="_blank" rel="noreferrer"
                                       style={{ color: 'var(--color-accent)' }}>ficha en {slug}</a>
                                  </span>
                                ))}
                              </div>
                            ))}
                          </div>
                        </details>
                      </td>
                      <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>
                        {g.tenants.map(nombreTenant).join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {encontradas.length > 0 && (
            <div style={{ marginTop: 'var(--space-3)', overflowX: 'auto' }}>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
                            paddingBottom: 'var(--space-1)' }}>
                Tiendas que SÍ sabemos leer — esto no es cola: el lector ya funciona y
                lo que falta es meter la tienda en el pipeline, así que suele ser el
                dinero más barato del tablero:
              </div>
              <table style={{ borderCollapse: 'collapse', fontSize: 'var(--text-sm)', width: '100%' }}>
                <thead>
                  <tr style={{ color: 'var(--color-text-3)', textAlign: 'left' }}>
                    {['Tienda', 'Dinero', '¿Incorporada al pipeline?', 'Convenios', 'Tenants'].map(h => (
                      <th key={h} style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)',
                                           fontSize: 'var(--text-xs)', fontWeight: 500,
                                           borderBottom: '1px solid var(--color-border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {encontradas.map(g => (
                    <tr key={g.tienda} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>{g.tienda}</td>
                      <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)',
                                   color: g.soportada === false ? 'var(--color-ok)' : 'var(--color-text-2)' }}>
                        {clp(g.monto)}
                        <Reparto g={g} />
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        {g.soportada === false
                          ? 'no — falta portarla, y su ficha ya se lee'
                          : g.soportada === true
                            ? 'sí — solo falta aprender el convenio'
                            : 'no se pudo comprobar'}
                      </td>
                      <td style={{ padding: '6px 10px' }}>{g.n_convenios}</td>
                      <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>
                        {g.tenants.map(nombreTenant).join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {av?.no_se_pudo_leer_que_tiendas_estan_soportadas && (
                <div style={{ marginTop: 'var(--space-1)', fontSize: 'var(--text-xs)',
                              color: 'var(--color-warn)' }}>
                  No se pudo leer qué tiendas están incorporadas, así que esta columna no
                  dice nada. No significa que ninguna lo esté.
                </div>
              )}
            </div>
          )}

          {sinTienda.length > 0 && (
            <div style={{ marginTop: 'var(--space-3)', overflowX: 'auto' }}>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
                            paddingBottom: 'var(--space-1)' }}>
                Sin tienda identificada — no es dinero perdido, es dinero que aún no se
                pudo atribuir:
              </div>
              <table style={{ borderCollapse: 'collapse', fontSize: 'var(--text-sm)', width: '100%' }}>
                <tbody>
                  {/* ⚠️ Hasta el 11-09 esta fila pintaba solo el total y el recuento
                      («$1.899 M · 11 convenio(s)»), y la lista —cuáles, de qué
                      tenant, si sigue siendo cliente— viajaba en el JSON sin que
                      nadie la viera. Es la pregunta entera de qué se puede
                      recuperar, y vivía donde no se lee (§10). */}
                  {sinTienda.map(g => (
                    <tr key={g.motivo} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '6px 10px' }}>{g.explicacion}</td>
                      <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>
                        {clp(g.monto)}
                        <Reparto g={g} />
                      </td>
                      <td style={{ padding: '6px 10px', fontSize: 'var(--text-xs)',
                                   color: 'var(--color-text-3)' }}>
                        <details>
                          <summary style={{ cursor: 'pointer' }}>
                            {g.n_convenios} convenio(s)
                            {Object.keys(g.tiendas_con_ficha_ilegible || {}).length > 0 && (
                              <> · {g.motivo === 'no_concluyente_ficha_enlazada_404'
                                     ? 'ficha enlazada que da 404 en'
                                     : 'ficha ilegible en'}{' '}
                                <span style={{ fontFamily: 'var(--font-mono)' }}>
                                  {Object.keys(g.tiendas_con_ficha_ilegible).join(', ')}</span>
                              </>
                            )}
                          </summary>
                          <div style={{ paddingTop: 'var(--space-1)', lineHeight: 1.6,
                                        color: 'var(--color-text-2)' }}>
                            {(g.convenios || []).map((c, i) => (
                              <div key={`${c.tenant}-${c.convenio}-${i}`}>
                                <span style={{ fontFamily: 'var(--font-mono)' }}>{c.convenio}</span>
                                {' · '}{c.titulo || '—'}
                                {' · '}<span style={{ fontFamily: 'var(--font-mono)' }}>
                                  {clp(c.monto)}</span>
                                {' · '}{nombreTenant(c.tenant)}
                                {' · '}{textoUltimaOC(c)}
                                {/* La evidencia, para ir a mirar: sin el enlace, un
                                    «404» o un «ilegible» obliga a creerse el veredicto. */}
                                {Object.entries(c.urls || {}).map(([slug, url]) => (
                                  <span key={slug}>
                                    {' · '}
                                    <a href={url} target="_blank" rel="noreferrer"
                                       style={{ color: 'var(--color-accent)' }}>ficha en {slug}</a>
                                  </span>
                                ))}
                              </div>
                            ))}
                          </div>
                        </details>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tot && tiendas.length === 0 && sinTienda.length === 0
            && encontradas.length === 0 && faltan.length === 0 && (
            <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)',
                          color: 'var(--color-text-3)' }}>
              No queda ningún convenio atascado en los {tot.n_tenants_sumados} tenants
              sondeados. Este cero sí está medido.
            </div>
          )}
        </div>

        <aside className="cm-nota-lateral" style={{ minWidth: 0 }}>
          <NotaAccion
            que="Suma, entre TODOS los tenants, el dinero que quedó atascado en cada tienda del portal que nuestro motor no sabe leer. Es lo que decide qué tienda conviene portar después: una tienda con $6 M en un tenant no justifica el trabajo, la misma en cinco tenants sumando $200 M, sí."
            coste="instantáneo — no toca el portal ni mide nada nuevo: solo lee lo que cada sondeo ya dejó guardado."
            mirar="Primero el aviso amarillo: dice sobre cuántos tenants se está sumando. Si faltan tenants, el total es un mínimo y no sirve para descartar una tienda. «Tienda identificada» significa que la tienda SÍ tiene el código y el problema es nuestro; «sin atribuir» es trabajo de búsqueda, no dinero perdido; y «tiendas que SÍ sabemos leer» no es cola — ahí el lector ya funciona y solo falta incorporarla, así que compara su dinero con el de arriba antes de decidir qué se porta."
          />
          {d && !d.error && d.lectura && (
            <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)',
                          borderTop: '1px solid var(--color-border)',
                          fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
                          lineHeight: 1.5 }}>
              {d.lectura}
            </div>
          )}
        </aside>
      </div>
    </div>
  )
}

// ── Mapa licitación → tienda ─────────────────────────────────────────────────
//
// 🔴 **La tabla de detrás es GLOBAL y acumulativa entre tenants.** Una fila
// equivocada la heredan todos los clientes, presentes y futuros, y nadie vuelve a
// mirarla. `app/services/sonda_convenios.py` —que sabe de sobra lo que ha
// medido— se prohíbe a sí misma escribirla por eso mismo. Esta pantalla hereda
// esa cautela, y su diseño entero sale de una frase: **fácil de rellenar bien y
// difícil de rellenar a ojo.**
//
// De ahí las tres decisiones que la gobiernan:
//
//   1. **La evidencia al lado del campo, no en la memoria de quien adopta.** El
//      sistema ya sabe qué licitación declara cada tienda y con qué título la
//      devolvió la API OCDS (`app/convenios.py`). Si la pantalla lo enseña,
//      adoptar es RECONOCER; si no, es teclear de memoria — y deducir el
//      convenio de su nombre es exactamente contra lo que avisa `convenios.py`.
//   2. **Nada se escribe de un solo clic.** Dos pasos, y el segundo enseña el
//      cuerpo exacto que va a viajar.
//   3. **`tienda: null` NO es «vacío»** — ver `_TIENDA_NULA` aquí abajo.
//
// ⚠️ Y una trampa del backend que esta pantalla esquiva y conviene no heredar:
// `LicitacionTiendaUpdate.tienda` es `Optional[str] = None`, así que un `PUT` con
// el cuerpo vacío marca la licitación como NO soportada **sin decir nada**. Aquí
// el desplegable empieza sin elegir y el botón no se enciende hasta que hay una
// elección, para que «no soportada» sea siempre una decisión y nunca un descuido.

// El valor que representa el veredicto «NO soportada». Es una cadena y no `null`
// a propósito: un `<option value={null}>` llega al manejador como la cadena
// "null" y se confundiría con un slug llamado así. Se traduce a `null` de verdad
// en un solo sitio, al enviar.
const _TIENDA_NULA = '__no_soportada__'

// 🔑 Los TRES estados, que no son dos. Fundir «sin fila» con «fila que declara
// null» es el error que este proyecto lleva toda la semana arreglando: `no_esta`
// fundía «la ficha ya no existe» con «no es de esta tienda» (16-09), y
// `SIN_PRICE_JSON` fundía «no tiene JSON de precios» con «no se midió nada»
// (15-09) — eso tuvo a `escritorio3` fuera cuatro meses.
function estadoDeFila(fila) {
  if (!fila) return 'sin_fila'
  return fila.tienda ? 'con_tienda' : 'no_soportada'
}

const ROTULO_ESTADO = {
  sin_fila:      { texto: 'sin fila — nadie lo ha decidido', color: 'var(--color-text-3)' },
  con_tienda:    { texto: 'adoptada',                        color: 'var(--color-ok)' },
  no_soportada:  { texto: 'NO soportada — medida, no vacía', color: 'var(--color-warn)' },
}

function MapaLicitaciones() {
  const [filas, setFilas] = useState(null)
  const [convenios, setConvenios] = useState([])
  const [error, setError] = useState('')
  const [licitacion, setLicitacion] = useState('')
  const [tienda, setTienda] = useState('')
  const [confirmando, setConfirmando] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [hecho, setHecho] = useState(null)

  const cargar = useCallback(async () => {
    setError('')
    try { setFilas(await getLicitaciones()) }
    catch (e) { setFilas([]); setError(e.response?.data?.detail || 'No se pudo leer el mapa') }
  }, [])

  // Se cargan solas: las dos son lecturas, no miden nada y no tocan el portal.
  useEffect(() => { cargar() }, [cargar])
  useEffect(() => {
    getConveniosConLicitacion()
      .then(cs => setConvenios(Array.isArray(cs) ? cs : []))
      .catch(() => setConvenios([]))
  }, [])

  const lic = licitacion.trim()
  const porLicitacion = Object.fromEntries(
    convenios.filter(c => c.licitacion).map(c => [c.licitacion, c]))
  const filaDe = Object.fromEntries((filas || []).map(f => [f.licitacion, f]))

  // La evidencia: qué dice el sistema de la licitación que se está escribiendo.
  const declarada = lic ? porLicitacion[lic] : null

  // El tercer estado, hecho visible: convenios que el sistema declara y que NO
  // tienen fila. Sin esta lista, «sin fila» es la ausencia de algo — y una
  // ausencia no se ve (§5). Es además la lista de adopciones pendientes.
  const sinFila = convenios.filter(c => c.licitacion && !filaDe[c.licitacion])

  const eligio = tienda !== ''
  const puedeEscribir = lic !== '' && eligio && !guardando

  async function escribir() {
    setGuardando(true); setError(''); setHecho(null)
    try {
      const r = await adoptarLicitacion(lic, tienda === _TIENDA_NULA ? null : tienda)
      setHecho(r)
      setConfirmando(false)
      setLicitacion(''); setTienda('')
      await cargar()
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo escribir la fila')
    } finally { setGuardando(false) }
  }

  return (
    <div style={{ marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)',
                  borderTop: '1px solid var(--color-border)' }}>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
        letterSpacing: '0.13em', textTransform: 'uppercase',
        color: 'var(--color-text-3)', paddingBottom: 'var(--space-2)',
      }}>
        Mapa licitación → tienda · global entre tenants
      </div>
      <div className="cm-rejilla-nota">
        <div style={{ minWidth: 0 }}>

          {error && (
            <div style={{ marginBottom: 'var(--space-3)', fontSize: 'var(--text-sm)',
                          color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>
              ✗ {error}
            </div>
          )}

          {/* ⚠️ `filas === null` es «aún no se ha leído», y NO se pinta como una
              tabla vacía: una tabla vacía se lee como «no hay ninguna fila», que
              es la conclusión contraria (§3). */}
          {filas === null && !error && (
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)' }}>
              Leyendo el mapa…
            </div>
          )}

          {filas !== null && filas.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 'var(--text-sm)', width: '100%' }}>
                <thead>
                  <tr style={{ color: 'var(--color-text-3)', textAlign: 'left' }}>
                    {['Licitación', 'Qué dice la fila', 'Tienda', 'Quién lo decidió'].map(h => (
                      <th key={h} style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)',
                                           fontSize: 'var(--text-xs)', fontWeight: 500,
                                           borderBottom: '1px solid var(--color-border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filas.map(f => {
                    const est = ROTULO_ESTADO[estadoDeFila(f)]
                    return (
                      <tr key={f.licitacion} style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>
                          {f.licitacion}
                        </td>
                        <td style={{ padding: '6px 10px', color: est.color }}>{est.texto}</td>
                        <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>
                          {f.tienda || '—'}
                        </td>
                        {/* Una fila puesta a mano tiene que VERSE puesta a mano:
                            dentro de tres meses, quien la lea necesita saber si
                            la aprendió el sistema o la decidió una persona. */}
                        <td style={{ padding: '6px 10px', fontSize: 'var(--text-xs)',
                                     color: f.origen === 'admin'
                                       ? 'var(--color-accent)' : 'var(--color-text-3)' }}>
                          {f.origen === 'admin' ? 'a mano (admin)'
                            : f.origen === 'seed' ? 'sembrada con el sistema'
                              : 'aprendida por el sistema'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {filas !== null && filas.length === 0 && !error && (
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)' }}>
              El mapa está vacío: ninguna licitación tiene fila todavía.
            </div>
          )}

          {/* El tercer estado, con nombre y apellidos. */}
          {sinFila.length > 0 && (
            <div style={{ marginTop: 'var(--space-3)', padding: 'var(--space-2) var(--space-3)',
                          borderRadius: 'var(--radius-sm)', background: 'var(--color-warn-soft)',
                          border: '1px solid var(--color-warn-line)',
                          fontSize: 'var(--text-sm)', lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--color-warn)' }}>
                Sin fila en el mapa ({sinFila.length}):
              </strong>{' '}
              el sistema declara estas licitaciones y la tabla no sabe nada de ellas.
              No es lo mismo que «NO soportada» — eso es un veredicto; esto es que
              nadie lo ha decidido.
              <div style={{ marginTop: 'var(--space-1)' }}>
                {sinFila.map(c => (
                  <div key={c.licitacion} style={{ display: 'flex', gap: 'var(--space-2)',
                                                   alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono)',
                                   color: 'var(--color-text)' }}>{c.licitacion}</span>
                    <span style={{ color: 'var(--color-text-2)' }}>→ {c.slug}</span>
                    <span style={{ color: 'var(--color-text-3)',
                                   fontSize: 'var(--text-xs)' }}>«{c.titulo}»</span>
                    <button
                      onClick={() => { setLicitacion(c.licitacion); setTienda(c.slug)
                                       setConfirmando(false); setHecho(null) }}
                      style={{
                        padding: '2px 10px', borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--text-xs)', border: '1px solid var(--color-border)',
                        background: 'transparent', color: 'var(--color-text-2)',
                        cursor: 'pointer', fontFamily: 'var(--font-ui)',
                      }}
                    >
                      preparar su adopción
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── El acto de escribir ─────────────────────────────────────────── */}
          <div style={{ marginTop: 'var(--space-4)', paddingTop: 'var(--space-3)',
                        borderTop: '1px solid var(--color-border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                          flexWrap: 'wrap' }}>
              <label htmlFor="licitacion-codigo"
                     style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-2)' }}>
                Licitación
              </label>
              <input
                id="licitacion-codigo"
                value={licitacion}
                onChange={e => { setLicitacion(e.target.value); setConfirmando(false) }}
                placeholder="p. ej. 2239-8-LR24"
                style={{
                  padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--color-border)', background: 'transparent',
                  color: 'var(--color-text)', fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-sm)', width: '180px',
                }}
              />
              <label htmlFor="licitacion-tienda"
                     style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-2)' }}>
                Tienda
              </label>
              {/* ⚠️ Empieza SIN elegir, y «NO soportada» es una opción explícita.
                  Si el desplegable naciera en «no soportada», el descuido más
                  barato del mundo escribiría un veredicto en una tabla global. */}
              <select
                id="licitacion-tienda"
                value={tienda}
                onChange={e => { setTienda(e.target.value); setConfirmando(false) }}
                style={{
                  padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--color-border)', background: 'transparent',
                  color: 'var(--color-text)', fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-sm)',
                }}
              >
                <option value="">— elige —</option>
                {convenios.map(c => (
                  <option key={c.slug} value={c.slug}>{c.slug}</option>
                ))}
                <option value={_TIENDA_NULA}>NO soportada (otro portal)</option>
              </select>
            </div>

            {/* La evidencia, al lado del campo. */}
            {lic !== '' && (
              <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-sm)',
                            lineHeight: 1.55,
                            color: declarada ? 'var(--color-text-2)' : 'var(--color-text-3)' }}>
                {declarada ? (
                  <>
                    <strong style={{ color: 'var(--color-ok)' }}>El sistema ya lo declara:</strong>{' '}
                    <span style={{ fontFamily: 'var(--font-mono)' }}>{lic}</span> es de{' '}
                    <span style={{ fontFamily: 'var(--font-mono)' }}>{declarada.slug}</span> —
                    «{declarada.titulo}». Ese título es el que devolvió la API OCDS, no uno
                    redactado a mano.
                  </>
                ) : (
                  <>Ninguna tienda del sistema declara <span style={{ fontFamily: 'var(--font-mono)' }}>
                    {lic}</span>. Eso no la hace falsa, pero aquí no hay evidencia que
                    reconocer: adoptar a ciegas una fila que heredan todos los tenants es
                    justo lo que esta pantalla existe para no hacer.</>
                )}
              </div>
            )}

            {!confirmando && (
              <button
                onClick={() => setConfirmando(true)}
                disabled={!puedeEscribir}
                title={!lic ? 'escribe la licitación'
                  : !eligio ? 'elige una tienda, o «NO soportada» si eso es lo que quieres declarar'
                    : undefined}
                style={{
                  marginTop: 'var(--space-3)',
                  padding: '7px 16px', borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--text-base)', border: '1px solid var(--color-border)',
                  background: 'transparent',
                  color: puedeEscribir ? 'var(--color-text-2)' : 'var(--color-text-3)',
                  cursor: puedeEscribir ? 'pointer' : 'default',
                  fontFamily: 'var(--font-ui)',
                }}
              >
                Escribir en el mapa…
              </button>
            )}

            {/* Segundo paso: el cuerpo exacto que va a viajar, y el aviso de que
                esto no es configuración de un cliente. */}
            {confirmando && (
              <div style={{ marginTop: 'var(--space-3)', padding: 'var(--space-3)',
                            borderRadius: 'var(--radius-sm)', background: 'var(--color-warn-soft)',
                            border: '1px solid var(--color-warn-line)',
                            fontSize: 'var(--text-sm)', lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--color-warn)' }}>
                  Esta fila la heredan TODOS los tenants, hoy y en el futuro.
                </strong>{' '}
                No es configuración de un cliente, y nadie vuelve a revisarla.
                <div style={{ marginTop: 'var(--space-2)', fontFamily: 'var(--font-mono)',
                              fontSize: 'var(--text-xs)', color: 'var(--color-text)' }}>
                  PUT /admin/licitaciones/{lic} · {'{'} "tienda": {
                    tienda === _TIENDA_NULA ? 'null' : `"${tienda}"`} {'}'}
                </div>
                <div style={{ marginTop: 'var(--space-2)', color: 'var(--color-text-2)' }}>
                  {tienda === _TIENDA_NULA
                    ? 'Vas a declararla NO SOPORTADA. Eso es un veredicto medido, no un hueco: el sistema dejará de re-sondearla.'
                    : `Vas a declarar que los productos de ${lic} viven en ${tienda}.`}
                </div>
                <div style={{ marginTop: 'var(--space-3)', display: 'flex',
                              gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                  <button
                    onClick={escribir}
                    disabled={guardando}
                    style={{
                      padding: '7px 16px', borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--text-base)', border: '1px solid var(--color-warn-line)',
                      background: 'transparent', color: 'var(--color-warn)',
                      cursor: guardando ? 'default' : 'pointer', fontFamily: 'var(--font-ui)',
                    }}
                  >
                    {guardando ? 'Escribiendo…' : 'Sí, escribir la fila'}
                  </button>
                  <button
                    onClick={() => setConfirmando(false)}
                    style={{
                      padding: '7px 16px', borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--text-base)', border: '1px solid var(--color-border)',
                      background: 'transparent', color: 'var(--color-text-2)',
                      cursor: 'pointer', fontFamily: 'var(--font-ui)',
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Qué quedó escrito, leído de la RESPUESTA del servidor y no de lo
                que se tecleó: si el backend guardara otra cosa, hay que verlo. */}
            {hecho && (
              <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)',
                            color: 'var(--color-ok)', lineHeight: 1.6 }}>
                ✓ Guardado:{' '}
                <span style={{ fontFamily: 'var(--font-mono)' }}>{hecho.licitacion}</span>
                {' → '}
                <span style={{ fontFamily: 'var(--font-mono)' }}>
                  {hecho.tienda || 'NO soportada'}</span>
                {' · origen '}
                <span style={{ fontFamily: 'var(--font-mono)' }}>{hecho.origen}</span>
              </div>
            )}
          </div>
        </div>

        <aside className="cm-nota-lateral" style={{ minWidth: 0 }}>
          <NotaAccion
            que="Dice en qué tienda del portal viven los productos de cada licitación de Convenio Marco. Es lo que convierte el dinero «sin atribuir» de la tabla de arriba en dinero que el motor sabe ir a buscar."
            coste="instantáneo de leer. Escribir una fila NO es gratis: la tabla es GLOBAL entre tenants, una fila equivocada la heredan todos los clientes y nadie vuelve a mirarla. Por eso se escribe en dos pasos."
            mirar="Tres estados, y no son dos. «Adoptada» tiene tienda; «NO soportada» es un VEREDICTO medido —vive en otro portal, no se vuelve a sondear—; y «sin fila» es que nadie lo ha decidido todavía. Fundir los dos últimos es el error que tuvo a escritorio3 fuera cuatro meses. Mira también quién decidió cada fila: «a mano» no se lee igual que «aprendida»."
          />
        </aside>
      </div>
    </div>
  )
}


// ── En qué paso se atasca nuestro lector en cada ficha ───────────────────────
// El sondeo dejó $410,5 M de manquehue en dos tiendas que SÍ tienen sus códigos
// y cuyo HTML no sabemos leer. Esto dice POR QUÉ, recorriendo los cinco pasos de
// `parsear_ficha` en su mismo orden — porque el motor devuelve el mismo null
// para los cinco y sin esto habría que elegir uno a ciegas.
function SondaFicha() {
  const [d, setD] = useState(null)
  const [cargando, setCargando] = useState(false)

  async function medir() {
    setCargando(true); setD(null)
    try { setD(await getSondaFicha()) }
    catch (e) { setD({ error: e.response?.data?.detail || 'No se pudo medir' }) }
    finally { setCargando(false) }
  }

  const fichas = Array.isArray(d?.fichas) ? d.fichas : []

  return (
    <div style={{ marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)',
                  borderTop: '1px solid var(--color-border)' }}>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
        letterSpacing: '0.13em', textTransform: 'uppercase',
        color: 'var(--color-text-3)', paddingBottom: 'var(--space-2)',
      }}>
        Qué publica cada ficha
      </div>
      <div className="cm-rejilla-nota">
        <div style={{ minWidth: 0 }}>
      <button
        onClick={medir}
        disabled={cargando}
        style={{
          padding: '7px 16px', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-base)',
          border: '1px solid var(--color-border)', background: 'transparent',
          color: 'var(--color-text-2)', cursor: cargando ? 'default' : 'pointer',
          fontFamily: 'var(--font-ui)',
        }}
      >
        {cargando ? 'Descargando las tres fichas…' : 'Medir las fichas de referencia'}
      </button>

      {d?.error && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)',
                      color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>✗ {d.error}</div>
      )}

      {fichas.length > 0 && (
        <div style={{ marginTop: 'var(--space-3)', overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 'var(--text-sm)', width: '100%' }}>
            <thead>
              <tr style={{ color: 'var(--color-text-3)', textAlign: 'left' }}>
                {['Tienda', '¿La lee?', 'Se para en', 'spConfig', 'dccpProductId',
                  'region_ids', 'Hijos'].map(h => (
                  <th key={h} style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)',
                                       fontSize: 'var(--text-xs)', fontWeight: 500,
                                       borderBottom: '1px solid var(--color-border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fichas.map(f => (
                <tr key={f.tienda} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>{f.tienda}</td>
                  <td style={{ padding: '6px 10px',
                               color: f.el_motor_la_lee ? 'var(--color-ok)' : 'var(--color-warn)' }}>
                    {f.error ? '—' : f.el_motor_la_lee ? 'sí' : 'no'}
                  </td>
                  <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)',
                               fontSize: 'var(--text-xs)' }}>
                    {f.error || f.paso_que_falla || '—'}
                  </td>
                  <td style={{ padding: '6px 10px' }}>{f.spConfig?.presente ? 'sí' : 'no'}</td>
                  <td style={{ padding: '6px 10px' }}>
                    {f.dccpProductId?.presente ? `sí (${f.dccpProductId.n_variantes})` : 'no'}
                  </td>
                  <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>
                    {f.spConfig ? `${f.spConfig.n_region_ids} · ${f.spConfig.region_ids_forma}` : '—'}
                  </td>
                  <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>
                    {f.hijos_resueltos ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* El inventario es la respuesta a «¿y qué publican en lugar de esto?»,
              que es lo que decide si portar la tienda son horas o semanas. */}
          <details style={{ marginTop: 'var(--space-3)' }}>
            <summary style={{ cursor: 'pointer', fontSize: 'var(--text-sm)',
                              color: 'var(--color-text-2)', fontFamily: 'var(--font-mono)' }}>
              Ver el inventario completo de cada ficha
            </summary>
            <BloqueJSON datos={d} nombre="sonda-fichas" maxAlto="520px" />
          </details>
        </div>
      )}
        </div>

        <aside className="cm-nota-lateral" style={{ minWidth: 0 }}>
          <NotaAccion
            que="Descarga tres fichas de producto concretas —dos que nuestro motor NO sabe leer y una que sí, como control— y dice en cuál de los cinco pasos de lectura se atasca cada una. Sirve para poner precio al trabajo de incorporar una tienda nueva: no es lo mismo que falte un mapa de regiones que que la tienda use otro motor entero."
            coste="menos de un minuto — tres peticiones. No cambia nada: solo lee."
            mirar="La fila de control (aseo3) DEBE salir con «¿La lee?» = sí; si sale que no, el fallo está en esta sonda y no en las tiendas. En las otras dos, «Se para en» es el dato: cuanto más tarde se atasque, más barato es arreglarlo."
          />
          {fichas.length > 0 && d.lectura && (
            <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)',
                          borderTop: '1px solid var(--color-border)',
                          fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
                          lineHeight: 1.5 }}>
              {d.lectura}
            </div>
          )}
        </aside>
      </div>
    </div>
  )
}


// ── Red de foto fija de `parsear_ficha` ──────────────────────────────────────
// `parsear_ficha` no tenía ni un test contra HTML real de tienda; esto captura una
// ficha real y la salida de hoy para que la suite exija mañana que sea idéntica.
// `test_toda_tienda_soportada_tiene_su_foto_fija` no deja declarar una tienda
// soportada sin su ficha congelada: primero la red, después el trapecio.
//
// ⚠️ `necesita_codigo` marca las tiendas de DOS EJES, donde una misma ficha
// contiene varios productos lógicos (emergencias202109: 16 regiones × 3
// situaciones = 48 variantes). Ahí `parsear_ficha` devuelve `null` si no se le
// dice CUÁL se busca — y lo hace a propósito, para no quedarse con 16 de esos 48
// códigos elegidos al azar y mandarlos al motor de precios.
//
// Por eso el botón se DESHABILITA sin código en vez de dejar capturar y explicar
// el fallo después: sin él la petición no da error, devuelve un fixture vacío, y
// versionar eso sería una red que no vigila nada (§5, el fallo que no grita).
//
// 🔴 `necesita_url` es la OTRA mitad, y costó una captura fallida descubrirla
// (21-09-2026). El código elige el producto **dentro** de una ficha; no sirve
// para encontrarla. Si no se da URL, el backend descubre una ficha cualquiera de
// la tienda — y en una tienda de varios productos por ficha, buscar ahí un código
// concreto es una moneda al aire con muy malas probabilidades. Se pidió el
// `2224559` (de `kit-aseo-2224555`) y volvió un arriendo de duchas con
// `esperado: null`, que se leyó como «el motor no sabe leer esta tienda».
//
// Así que las dos condiciones van juntas pero NO son la misma: una dice qué
// producto, la otra en qué página. Escribirlas como un solo campo obligaría a
// dar URL en tiendas de un eje, donde cualquier ficha sirve y descubrirla es
// justo la comodidad que hace que la red se pueda rehacer sin buscar enlaces.
const TIENDAS_A_CAPTURAR = [
  { slug: 'alimentos2',        necesita_codigo: false, necesita_url: false },
  { slug: 'ferreteria2',       necesita_codigo: false, necesita_url: false },
  { slug: 'aseo3',             necesita_codigo: false, necesita_url: false },
  { slug: 'escritorio3',       necesita_codigo: false, necesita_url: false },
  { slug: 'emergencias202109', necesita_codigo: true,  necesita_url: true  },
]

function FotoFija() {
  const [d, setD] = useState(null)
  const [cargando, setCargando] = useState('')
  const [codigo, setCodigo] = useState('')
  const [url, setUrl] = useState('')
  const [yaHechas, setYaHechas] = useState([])

  // Se carga sola: no mide nada, solo lee ficheros del repositorio. Y tiene que
  // estar ANTES de capturar, no después: es lo que dice qué ficha se congeló en
  // su día, y recapturar otra distinta sin querer cambia lo que la red defiende.
  useEffect(() => {
    getFotoFijaVersionadas()
      .then(r => setYaHechas(Array.isArray(r?.fichas) ? r.fichas : []))
      .catch(() => setYaHechas([]))
  }, [])

  const urlLimpia = url.trim()

  // ⚠️ Una URL escrita para una tienda y un botón pulsado en otra producen un
  // fixture con `tienda: "alimentos2"` y dentro la ficha de emergencias. Eso es
  // PEOR que una captura vacía: la vacía se ve y se descarta, ésta pasa todas las
  // validaciones y envenena la red. El slug viaja siempre en la ruta de la ficha,
  // así que la discrepancia se puede ver antes de pulsar.
  const urlEsDeOtraTienda = slug => urlLimpia !== '' && !urlLimpia.includes(`/${slug}/`)

  async function capturar(tienda, conCodigo) {
    setCargando(tienda); setD(null)
    // El código viaja SOLO donde hace falta: en una tienda de un eje se ignora,
    // pero mandarlo igualmente metería un `codigo` en su fixture que invitaría a
    // creer que ahí también decide algo.
    // La URL, en cambio, viaja SIEMPRE que se haya escrito: también en las tiendas
    // de un eje sirve para rehacer la foto de una ficha concreta en vez de la que
    // toque descubrir ese día.
    try { setD(await getFotoFija(tienda, urlLimpia, conCodigo ? codigo.trim() : '')) }
    catch (e) { setD({ error: e.response?.data?.detail || 'No se pudo capturar' }) }
    finally { setCargando('') }
  }

  return (
    <div style={{ marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)',
                  borderTop: '1px solid var(--color-border)' }}>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
        letterSpacing: '0.13em', textTransform: 'uppercase',
        color: 'var(--color-text-3)', paddingBottom: 'var(--space-2)',
      }}>
        Red de foto fija del lector de fichas
      </div>
      {/* La misma rejilla que las otras dos sondas (Manuel, 31-08, opción C):
          esta llegó del hilo del motor mientras se reordenaba la pestaña. */}
      <div className="cm-rejilla-nota">
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {TIENDAS_A_CAPTURAR.map(({ slug, necesita_codigo, necesita_url }) => {
              const sinCodigo = necesita_codigo && !codigo.trim()
              const sinUrl = necesita_url && urlLimpia === ''
              const otraTienda = urlEsDeOtraTienda(slug)
              const frena = sinCodigo || sinUrl || otraTienda
              // Un solo motivo a la vez, y el más concreto primero: un botón gris
              // sin decir por qué es un botón roto (§5).
              const porQue = otraTienda
                ? `la URL escrita no es de ${slug} — se guardaría su ficha con el nombre de esta tienda`
                : sinUrl
                  ? `${slug} tiene varios productos por ficha: pega abajo la URL de la que contiene tu código, o se descargaría otra al azar`
                  : sinCodigo
                    ? `${slug} tiene varios productos por ficha: escribe abajo el código CM que buscas, o la captura saldría vacía`
                    : undefined
              return (
                <button
                  key={slug}
                  onClick={() => capturar(slug, necesita_codigo)}
                  disabled={!!cargando || frena}
                  title={porQue}
                  style={{
                    padding: '7px 16px', borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--text-base)', border: '1px solid var(--color-border)',
                    background: 'transparent',
                    color: frena ? 'var(--color-text-3)' : 'var(--color-text-2)',
                    cursor: (cargando || frena) ? 'default' : 'pointer',
                    fontFamily: 'var(--font-ui)',
                  }}
                >
                  {cargando === slug ? `Capturando ${slug}…` : `Capturar ${slug}`}
                </button>
              )
            })}
          </div>

          {/* Los dos campos van SIEMPRE visibles, no solo al elegir una tienda de
              dos ejes: ese orden obligaría a pulsar un botón deshabilitado para
              enterarse de que hacen falta. Así la condición se lee antes de
              intentarlo. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                        marginTop: 'var(--space-3)', flexWrap: 'wrap' }}>
            <label htmlFor="foto-fija-codigo"
                   style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-2)' }}>
              Código CM
            </label>
            <input
              id="foto-fija-codigo"
              value={codigo}
              onChange={e => setCodigo(e.target.value)}
              placeholder="p. ej. 1355789"
              style={{
                padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--color-border)', background: 'transparent',
                color: 'var(--color-text)', fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-sm)', width: '150px',
              }}
            />
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)' }}>
              qué PRODUCTO se lee dentro de la ficha — solo en tiendas con varios
              por ficha (emergencias202109)
            </span>
          </div>

          {/* 🔴 El campo que faltaba. Estuvo en el API desde el primer día y sin
              él la captura de una tienda de dos ejes no podía salir bien: el
              código no encuentra la ficha, solo elige dentro de ella. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                        marginTop: 'var(--space-2)', flexWrap: 'wrap' }}>
            <label htmlFor="foto-fija-url"
                   style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-2)' }}>
              URL de la ficha
            </label>
            <input
              id="foto-fija-url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://conveniomarco2.mercadopublico.cl/…"
              style={{
                padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--color-border)', background: 'transparent',
                color: 'var(--color-text)', fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-sm)', width: 'min(100%, 460px)',
              }}
            />
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)' }}>
              QUÉ PÁGINA se descarga. Vacío = se descubre una ficha cualquiera de
              la tienda, que en las de un eje vale y en las de varios casi nunca
              es la que buscas. La sonda de convenios da la URL exacta.
            </span>
          </div>

          {/* 🔴 Los dos datos que hacen falta para REHACER la red, que hasta hoy
              vivían solo dentro del fixture —un blob comprimido de 300 KB en un
              fichero del repositorio— y por tanto en ningún sitio legible (§10).
              Sin esto, `necesita_url` convierte «recapturar emergencias202109»
              en «pídele a alguien que te abra un fichero», y una red que cuesta
              rehacer se queda defendiendo un HTML que el portal ya cambió. */}
          {yaHechas.length > 0 && (
            <details style={{ marginTop: 'var(--space-3)' }}>
              <summary style={{ cursor: 'pointer', fontSize: 'var(--text-sm)',
                                color: 'var(--color-text-2)' }}>
                Qué ficha tiene congelada cada tienda ({yaHechas.length}) — para rehacerla
              </summary>
              <div style={{ paddingTop: 'var(--space-2)', fontSize: 'var(--text-sm)',
                            lineHeight: 1.7 }}>
                {yaHechas.map(f => (
                  <div key={f.tienda} style={{ display: 'flex', gap: 'var(--space-2)',
                                               alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono)',
                                   color: 'var(--color-text)' }}>{f.tienda}</span>
                    {f.codigo && (
                      <span style={{ fontFamily: 'var(--font-mono)',
                                     color: 'var(--color-text-3)' }}>código {f.codigo}</span>
                    )}
                    {/* ⚠️ Una foto sin salida no vigila nada, y desde aquí no se
                        vería: el fixture está, el fichero pesa, y todo parece en
                        orden. Se dice donde se mira la lista. */}
                    {f.esperado_vacio && (
                      <span style={{ color: 'var(--color-error)' }}>
                        ⚠ se capturó VACÍA — no vigila nada
                      </span>
                    )}
                    <button
                      onClick={() => { setUrl(f.url || ''); setCodigo(f.codigo || '') }}
                      style={{
                        padding: '2px 10px', borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--text-xs)', border: '1px solid var(--color-border)',
                        background: 'transparent', color: 'var(--color-text-2)',
                        cursor: 'pointer', fontFamily: 'var(--font-ui)',
                      }}
                    >
                      usar estos datos
                    </button>
                    <a href={f.url} target="_blank" rel="noreferrer"
                       style={{ color: 'var(--color-accent)', fontSize: 'var(--text-xs)',
                                wordBreak: 'break-all' }}>{f.url}</a>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Que se apaguen cuatro botones al pegar una URL es correcto, pero sin
              decirlo se lee como una avería. La ausencia de algo no se ve (§5). */}
          {urlLimpia !== '' && (
            <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-sm)',
                          lineHeight: 1.55,
                          color: TIENDAS_A_CAPTURAR.some(t => !urlEsDeOtraTienda(t.slug))
                            ? 'var(--color-text-3)' : 'var(--color-warn)' }}>
              {TIENDAS_A_CAPTURAR.some(t => !urlEsDeOtraTienda(t.slug))
                ? `Con esa URL puesta solo se puede capturar ${
                    TIENDAS_A_CAPTURAR.filter(t => !urlEsDeOtraTienda(t.slug))
                      .map(t => t.slug).join(', ')
                  }: los demás botones se apagan para no guardar esa ficha con el nombre de otra tienda. Vacía el campo para volver a capturarlas.`
                : 'Esa URL no es de ninguna de estas tiendas — el slug de la tienda va siempre en su ruta. Compruébala, o vacía el campo.'}
            </div>
          )}

          {d?.error && (
            <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)',
                          color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>✗ {d.error}</div>
          )}

          {d && !d.error && (
            <>
              <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)',
                            lineHeight: 1.6, color: 'var(--color-text-2)' }}>
                <b>{d.tienda}</b> · {(d.bytes_original || 0).toLocaleString('es-CL')} bytes
                {' → '}{(d.bytes_guardado || 0).toLocaleString('es-CL')} ·{' '}
                <span style={{ color: d.reduccion_fiel ? 'var(--color-ok)' : 'var(--color-warn)' }}>
                  reducción {d.reduccion_fiel ? 'fiel' : 'NO fiel (viaja el original)'}
                </span>{' · '}
                <span style={{ color: d.esperado ? 'var(--color-ok)' : 'var(--color-error)' }}>
                  {d.esperado
                    ? `${Object.keys(d.esperado.codigos_hijos || {}).length} regiones leídas`
                    : (d.codigo
                        ? `el motor NO lee esta ficha con el código ${d.codigo}`
                        : 'el motor NO lee esta ficha — si tiene varios productos por ficha, dale su código CM')}
                </span>
              </div>

              {/* 🔴 El aviso lo redacta el BACKEND, que es el único que sabe si la
                  ficha se eligió o se descubrió (§4: una regla, un sitio). Aquí
                  solo se pinta. Sin esto, un `esperado: null` sobre una ficha
                  descubierta al azar se lee como «el motor no sabe leer esta
                  tienda» — que es la conclusión contraria y la cara. */}
              {d.ficha_al_azar_con_codigo && (
                <div style={{
                  marginTop: 'var(--space-2)', padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-sm)', background: 'var(--color-warn-soft)',
                  border: '1px solid var(--color-warn-line)',
                  fontSize: 'var(--text-sm)', lineHeight: 1.55, color: 'var(--color-text-2)',
                }}>
                  <strong style={{ color: 'var(--color-warn)' }}>
                    Esto NO dice que el motor no sepa leer la tienda.
                  </strong>{' '}
                  {d.ficha_al_azar_con_codigo}
                </div>
              )}

              {/* La URL de lo que se bajó de verdad. Si se descubrió, es el dato
                  que explica el resultado; si se eligió, confirma que llegó. */}
              <div style={{ marginTop: 'var(--space-1)', fontSize: 'var(--text-xs)',
                            color: 'var(--color-text-3)', wordBreak: 'break-all' }}>
                {d.url_descubierta === true
                  ? 'ficha descubierta (no se dio URL): '
                  : d.url_descubierta === false ? 'ficha que pediste: ' : 'ficha: '}
                <a href={d.url} target="_blank" rel="noreferrer"
                   style={{ color: 'var(--color-accent)' }}>{d.url}</a>
              </div>
              <details style={{ marginTop: 'var(--space-3)' }} open>
                <summary style={{ cursor: 'pointer', fontSize: 'var(--text-sm)',
                                  color: 'var(--color-text-2)', fontFamily: 'var(--font-mono)' }}>
                  El fixture, para pegar en el chat
                </summary>
                {/* Compacto a propósito: es un fixture para pegar, y la sangría
                    lo hincharía sin aportar nada a quien lo va a versionar. */}
                <BloqueJSON datos={d} nombre={`foto-fija-${d.tienda || 'tienda'}`}
                            maxAlto="320px" compacto />
              </details>
            </>
          )}
        </div>

        <aside className="cm-nota-lateral" style={{ minWidth: 0 }}>
          <NotaAccion
            que="Descarga una ficha real de esa tienda y guarda, junto a ella, lo que nuestro lector devuelve HOY. Con eso la suite puede exigir mañana que devuelva exactamente lo mismo — que es la única garantía de que tocar el motor no rompa lo que ya funciona."
            coste="unos segundos, una o dos peticiones. No cambia nada: solo lee. Hay que hacerlo una vez por tienda."
            mirar="`reduccion_fiel` debe ser `true` y `esperado` NO puede venir vacío: una foto de una ficha que el motor no lee no vigilaría nada. Después, pásame el JSON entero —con el botón Copiar del recuadro— y yo lo versiono."
          />
        </aside>
      </div>
    </div>
  )
}


/** La ventana en barras: los mismos días de la línea, apilados OK/error.
    Pedido por Manuel (27-08) al retirar la tarjeta Actividad de Estado: la
    lectura gráfica se muda aquí, junto al resumen, y respeta los filtros
    activos porque sale de los MISMOS datos que la lista. */
function GraficoVentana({ dias }) {
  const data = [...dias]
    .sort((a, b) => a.fecha.localeCompare(b.fecha))
    .map(d => {
      const [y, m, dd] = d.fecha.split('-').map(Number)
      return {
        fecha: d.fecha,
        ok: d.resumen.ok || 0,
        error: d.resumen.error || 0,
        name: new Date(y, m - 1, dd).toLocaleDateString('es-CL', { weekday: 'short' }),
      }
    })
  if (!data.some(d => d.ok + d.error > 0)) return null

  return (
    <div style={{ marginBottom: 'var(--space-5)' }}>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
        letterSpacing: '0.11em', textTransform: 'uppercase',
        color: 'var(--color-text-3)', marginBottom: 'var(--space-2)',
      }}>
        La ventana en barras
      </div>
      <ResponsiveContainer width="100%" height={110}>
        <BarChart data={data} margin={{ top: 4, right: 0, left: -20, bottom: 0 }}>
          <XAxis
            dataKey="name"
            tick={{ fill: 'var(--color-text-3)', fontSize: 11, fontFamily: 'var(--font-mono)' }}
            axisLine={false} tickLine={false}
          />
          <YAxis hide allowDecimals={false} />
          <Tooltip
            contentStyle={{
              background: 'var(--color-surface)', border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-sm)',
              fontFamily: 'var(--font-mono)', color: 'var(--color-text)',
            }}
            cursor={{ fill: 'var(--color-cursor)' }}
            labelFormatter={(_, payload) => payload?.[0]?.payload?.fecha || ''}
          />
          <Bar dataKey="ok" name="Correctas" stackId="r" fill="var(--color-ok)" />
          <Bar dataKey="error" name="Con error" stackId="r"
               fill="var(--color-error)" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default function Ejecuciones() {
  const navigate = useNavigate()
  // Los filtros llegan también por la URL: las alertas de Estado aterrizan
  // aquí con el contexto puesto (?tenant=X&error=1) en vez de en una pantalla
  // genérica que llega vacía (Manuel, 27-08). ?pestana= aterriza en la suya
  // (la tarjeta Resumen semanal de Estado manda a ?pestana=correo).
  const [searchParams] = useSearchParams()
  const pedida = searchParams.get('pestana')
  const [pestana, setPestana] = useState(
    PESTANAS.some(p => p.clave === pedida) ? pedida : 'linea'
  )
  const [tenant, setTenant] = useState(searchParams.get('tenant') || null)
  const [tipo, setTipo] = useState(null)
  const [soloError, setSoloError] = useState(searchParams.get('error') === '1')
  const [ventana, setVentana] = useState(7)
  // Días con el pliegue alternado a mano. Se guarda el CAMBIO respecto al
  // defecto (hoy y ayer abiertos, el resto plegado), no el estado absoluto:
  // así, al cambiar la ventana, los días nuevos llegan con su defecto.
  const [alternados, setAlternados] = useState(() => new Set())

  const { data, isLoading, isError, error, refetch } =
    useEjecuciones({ tenant, tipo, soloError, dias: ventana })

  const tenants = data?.tenants ?? []
  const proximas = data?.proximas ?? []
  const dias = data?.dias ?? []
  const resumen = data?.resumen ?? []
  // El AHORA se pinta en hora de Chile (29-08): la marca viaja en UTC de la casa.
  const horaAhora = data?.ahora ? horaCL(data.ahora) : null

  // El motivo del fallo, para decirlo en pantalla: sin él, una lectura rota se
  // pintaba como «Nada programado · Sin ejecuciones · 0 activos» — tres
  // afirmaciones en falso (el §3/§5 aplicado a la página entera; 28-08).
  // El interceptor de api/client.js reenvuelve los 5xx (status/data van en el
  // Error, no en .response), así que se miran los dos sitios.
  const estadoHttp = error?.status ?? error?.response?.status
  const detalleHttp = error?.data?.detail ?? error?.response?.data?.detail
  const motivoFallo = isError
    ? (estadoHttp
        ? `HTTP ${estadoHttp}`
          + (typeof detalleHttp === 'string' ? ` · ${detalleHttp}` : '')
        : (error?.message || 'sin respuesta del servidor')
      ).replace(/\.+$/, '')
    : null

  const alternar = fecha => setAlternados(s => {
    const n = new Set(s)
    if (n.has(fecha)) n.delete(fecha); else n.add(fecha)
    return n
  })

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title="Ejecuciones"
        subtitle="lo que viene y lo que pasó, en la misma línea"
        action={pestana === 'linea'
          ? <SelectorTenant tenants={tenants} valor={tenant} onCambio={setTenant}
                            cargando={isLoading} fallo={isError}
                            total={data?.tenants_total ?? null} />
          : null}
      />

      <BarraPestanas pestanas={PESTANAS} activa={pestana} onCambio={setPestana} />

      {pestana === 'linea' && (
      <div style={{
        padding: 'var(--space-3) var(--space-6)',
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'center',
      }}>
        {TIPOS.map(t => (
          <Pildora key={t.etiqueta} activa={tipo === t.clave} onClick={() => setTipo(t.clave)}>
            {t.etiqueta}
          </Pildora>
        ))}
        <Pildora activa={soloError} onClick={() => setSoloError(v => !v)}>
          solo con error
        </Pildora>
        <span style={{ width: '1px', height: '16px', background: 'var(--color-border)',
                       margin: '0 var(--space-2)' }} />
        {VENTANAS.map(v => (
          <Pildora key={v} activa={ventana === v} onClick={() => setVentana(v)}>
            {v} días
          </Pildora>
        ))}
      </div>
      )}

      {pestana === 'linea' && (
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-6)' }}>
        {/* Dos columnas (Manuel, 28-08): la línea a la izquierda y el resumen
            en una columna FIJA a la derecha — pegajosa dentro del scroll, para
            que las cifras y las barras se vean siempre, filtres lo que filtres.

            Sin tope de ancho (Manuel, 31-08, 2ª ronda): el 1220px dejaba 416 px
            de pantalla vacíos a 1920 —medido— mientras la línea apretaba sus
            columnas. La columna de la derecha ya es fija, así que todo lo que
            se recupera se lo lleva la línea, que es quien lo necesita. */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px',
          gap: 'var(--space-7)', alignItems: 'start',
        }}>
        <div style={{ minWidth: 0 }}>

          {isLoading && (
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
              color: 'var(--color-text-3)',
            }}>
              Leyendo las ejecuciones…
            </div>
          )}

          {/* Un fallo se dice, con su motivo — no se pinta como página vacía. */}
          {isError && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-4)',
              padding: 'var(--space-4)', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-error-line)',
              background: 'var(--color-error-soft)',
            }}>
              <div style={{
                flex: 1, fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
                color: 'var(--color-error)', lineHeight: 1.5,
              }}>
                ✗ No se pudo leer las ejecuciones — {motivoFallo}. Lo que ves
                abajo no es «no hay nada»: es que no hay lectura.
              </div>
              <button
                onClick={() => refetch()}
                style={{
                  padding: '7px 16px', borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--text-base)', fontFamily: 'var(--font-ui)',
                  border: '1px solid var(--color-border)', background: 'transparent',
                  color: 'var(--color-text-2)', cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                Reintentar
              </button>
            </div>
          )}

          {!isLoading && !isError && (
            <>
              {/* ── lo próximo ── */}
              <div>
                <div style={{
                  fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
                  letterSpacing: '0.13em', textTransform: 'uppercase',
                  color: 'var(--color-text-3)', paddingBottom: 'var(--space-1)',
                }}>
                  Lo próximo
                </div>

                {/* Sin planificador no se puede afirmar que no hay nada programado:
                    son cosas distintas y confundirlas es el §3. */}
                {data?.scheduler_legible === false ? (
                  <div style={{
                    fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
                    color: 'var(--color-warn)', padding: 'var(--space-3) 0',
                  }}>
                    No se pudo leer el planificador — esto no significa que no haya nada programado
                  </div>
                ) : proximas.length === 0 ? (
                  <div style={{
                    fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
                    color: 'var(--color-text-3)', padding: 'var(--space-3) 0',
                  }}>
                    {tenant ? 'Este tenant no tiene nada programado' : 'Nada programado'}
                  </div>
                ) : (
                  proximas.map(p => <Proxima key={p.clave} dato={p} />)
                )}
              </div>

              {/* ── el pliegue ── */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-4)',
                margin: 'var(--space-5) 0 var(--space-1)',
              }}>
                <span style={{
                  fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
                  color: 'var(--color-accent)', letterSpacing: '0.12em',
                }}>
                  AHORA{horaAhora ? ` · ${horaAhora}` : ''}
                </span>
                <span style={{
                  flex: 1, height: '1px', background: 'var(--color-accent)',
                  opacity: 0.35, display: 'block',
                }} />
              </div>

              {/* ── lo que pasó ── */}
              {dias.length === 0 ? (
                <div style={{
                  fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
                  color: 'var(--color-text-3)', padding: 'var(--space-5) 0',
                }}>
                  {soloError
                    ? 'Ninguna ejecución falló en la ventana'
                    : 'Sin ejecuciones en la ventana'}
                </div>
              ) : dias.map(d => {
                // Un día viejo llega plegado a esta única línea (fecha +
                // resumen) y se abre al clic; hoy y ayer llegan abiertos.
                const abierta = _esReciente(d.fecha) !== alternados.has(d.fecha)
                return (
                <div key={d.fecha} style={{ marginTop: 'var(--space-5)' }}>
                  <div
                    onClick={() => alternar(d.fecha)}
                    style={{
                      display: 'flex', alignItems: 'baseline',
                      justifyContent: 'space-between', paddingBottom: 'var(--space-1)',
                      cursor: 'pointer', userSelect: 'none',
                    }}
                  >
                    <span style={{
                      fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
                      letterSpacing: '0.13em', textTransform: 'uppercase',
                      color: 'var(--color-text-3)',
                    }}>
                      <span style={{ display: 'inline-block', width: '14px' }}>
                        {abierta ? '▾' : '▸'}
                      </span>
                      {_rotuloDia(d.fecha)}
                    </span>
                    <span style={{
                      fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
                      color: 'var(--color-text-3)',
                    }}>
                      {[
                        d.resumen.ok ? `${d.resumen.ok} correctas` : null,
                        d.resumen.error ? `${d.resumen.error} con error` : null,
                        d.resumen.running ? `${d.resumen.running} corriendo` : null,
                      ].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                  {abierta && d.filas.map(f => (
                    <Pasada key={f.clave} dato={f}
                            onIr={id => navigate(`/ejecuciones/${id}`)} />
                  ))}
                </div>
                )
              })}

            </>
          )}
        </div>

        {/* ── la columna fija: cifras arriba, barras debajo ── */}
        <div style={{
          position: 'sticky', top: 0, alignSelf: 'start',
          display: 'flex', flexDirection: 'column', gap: 'var(--space-5)',
        }}>
          {resumen.map(c => (
            <div key={c.clave}>
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
                letterSpacing: '0.11em', textTransform: 'uppercase',
                color: 'var(--color-text-3)', marginBottom: 'var(--space-2)',
              }}>
                {c.etiqueta}
              </div>
              <Cifra valor={c.valor} lectura={c.lectura}
                     nada="ninguna" tamano="var(--text-xl)" />
            </div>
          ))}
          {dias.length > 0 && <GraficoVentana dias={dias} />}
        </div>
        </div>
      </div>
      )}

      {/* El correo semanal es una ejecución más, pero su REGISTRO —quién lo
          recibió y quién no— no cabe en una fila de la línea. Su pestaña es
          adonde manda la tarjeta Resumen semanal de Estado. */}
      {/* El mismo ancho que la Línea de tiempo, que desde el 31-08 (2ª ronda)
          es TODO el ancho: primero fue 880px, luego 1220px, y las dos veces la
          tabla del correo —quién lo recibió y quién no— se estrechaba con
          pantalla libre al lado. Se quita el tope en vez de subirlo otra vez. */}
      {/* Plantillas: a quién le llegó el código nuevo. Pestaña propia y no un
          bloque dentro de otra, porque es la pregunta que ninguna de las tres
          responde — y la que se hace justo DESPUÉS de regenerar, que se hace
          desde aquí. Nace del 31-08-2026: nueve tenants regenerados sin ver
          ningún cambio, y ninguna forma de comprobarlo sin lanzar una petición
          con token a mano. */}
      {pestana === 'plantillas' && (
        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-6)' }}>
          <EstadoPlantillas />
        </div>
      )}

      {/* Va aquí, junto a «Plantillas», por la misma razón: las dos son revisiones
          CRUZADAS de todos los tenants que se hacen después de un cambio, y las
          dos diagnostican sin actuar — se actúa en la ficha del cliente. Ponerlas
          en sitios distintos obligaría a recordar en cuál de los dos se mira qué. */}
      {pestana === 'competidores' && (
        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-6)' }}>
          <RevisionCompetidores />
        </div>
      )}

      {pestana === 'correo' && (
        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-6)' }}>
          <ResumenSemanal />
        </div>
      )}

      {/* Sin tope de ancho (Manuel, 31-08): el 1220px venía de la era de una
          columna y dejaba media pantalla vacía mientras las tablas —de 5 y 7
          columnas— se estrechaban. Aquí manda el ancho de la pantalla. */}
      {pestana === 'sonda' && (
        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-6)' }}>
          <SondaTiendas />
        </div>
      )}
    </div>
  )
}
